/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C2b §5] THE FIVE COMMAND RANK GROUPS.
 *
 *  A command role's allowed ranks is a MIXED list: group references AND individual codes, in
 *  one field. `{group: AC}` means any Assistant Commissioner INCLUDING ONE ADDED NEXT YEAR;
 *  `{rank: 'AC(W)'}` means that rank alone, for ever. Storing five codes and calling it a group
 *  loses that distinction permanently, and §5f forbids inferring it back — a policy listing all
 *  five AC variants is NOT to be re-read as "they meant the group".
 *
 *  ⚠️ MEMBERSHIP IS BY RANK **CODE**, NEVER BY ObjectId AND NEVER BY POSITION.
 *  [RANKS] settled that a reference master keys on the client's own immutable abbreviation —
 *  `ranks.code` IS the `RANK_OF` printed on their posting orders — so a group declared against
 *  codes survives a reseed, a re-import and a differently-ordered master. An id list would be
 *  environment-specific and could not live in a committed constant at all.
 *
 *  ⚠️ IT LIVES IN `constants/`, WHICH IS ZOD-FREE, so `pwa/` could import it inside its 150 KB
 *  budget if it ever needs to — the rule `officerApp.constant.ts` was moved here for.
 *
 *  ⚠️ SERGEANT AND SUBEDAR ARE DELIBERATELY OUTSIDE ALL FIVE, and that is a client decision
 *  rather than an omission. They remain FORCE posts under `SI/Sgt./Subedar` and cannot command;
 *  `seed.test.ts` asserts they carry no command flag.
 *
 *  ⚠️ AND POST TYPES ARE OUT OF SCOPE (§5h). They keep their own eligible-ranks list and drive
 *  the force grid. The meeting note "start rank groups from Assistant Sub-Inspectors" is
 *  UNRESOLVED and is raised as a question rather than guessed at — extending these downward
 *  would make an ASI command-eligible, which is a change to WHO MAY COMMAND.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export const RANK_GROUP_KEYS = ['CP', 'DC', 'AC', 'INSPECTOR', 'SI'] as const;

export type RankGroupKey = (typeof RANK_GROUP_KEYS)[number];

export type RankGroup = {
  readonly key: RankGroupKey;
  /** What the picker shows. The rank master's own words, shortened to a family name. */
  readonly label: string;
  /**
   * ⚠️ THE CLIENT'S OWN CODES. `ranks.code` may contain `/`, `(` and `)` — `A/ASI`, `AC(W)`,
   * `INSPR(TECH)` — because it is transcribed from their establishment and nothing derives an
   * identifier, a URL segment or a class name from it. [RANKS] records what minting our own
   * cost the last time somebody tried.
   */
  readonly rankCodes: readonly string[];
};

/**
 * ⚠️ ORDERED BY SENIORITY, MOST SENIOR FIRST, and that order is what the picker renders.
 * It is declaration order rather than a stored rank — `ORG_UNIT_SUBTYPES`' own caveat applies:
 * nothing may sort on the SPELLING of these keys.
 */
export const RANK_GROUPS = [
  { key: 'CP', label: 'Commissioner', rankCodes: ['CP', 'ADDL_CP', 'JT_CP'] },
  { key: 'DC', label: 'Deputy Commissioner', rankCodes: ['DC', 'ADDL_DC'] },
  { key: 'AC', label: 'Assistant Commissioner', rankCodes: ['AC', 'AC(IN)', 'AC(NON)', 'AC(TECH)', 'AC(W)'] },
  {
    key: 'INSPECTOR',
    label: 'Inspector',
    rankCodes: ['INSPR', 'INSPR(IN)', 'INSPR(NON)', 'INSPR(TECH)', 'INSPR(W)'],
  },
  { key: 'SI', label: 'Sub-Inspector', rankCodes: ['SI', 'SI(A)', 'SI(W)'] },
] as const satisfies readonly RankGroup[];

/** Every code any group claims. The set `seed.test.ts` measures the command flag against. */
export const RANK_GROUP_MEMBER_CODES: readonly string[] = RANK_GROUPS.flatMap((group) => group.rankCodes);

export const isRankGroupKey = (value: string): value is RankGroupKey =>
  (RANK_GROUP_KEYS as readonly string[]).includes(value);

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C2b §5g] THE ONE EXPANSION, AND BOTH SIDES OF THE RULE CALL IT.
 *
 *  A command role's eligible set reaches TWO places that must agree:
 *    · the OFFERED set — `OfficerPicker` narrows `GET /officers?rankIds=` with it;
 *    · the ACCEPTED set — `assertCommandOfficersEligible` refuses an officer outside it.
 *
 *  `eligibleRankIdsFor` is this repository's precedent for exactly that pair, and its stated
 *  property is the one that matters: teaching ONE function makes the search and the refusal
 *  agree automatically, so NEITHER CALL SITE CHANGES. Two copies drift, and the visible cost is
 *  a sheet that offers an officer and then refuses them — the operator is told their own screen
 *  was wrong.
 *
 *  ⚠️ IT TAKES A CODE→ID MAP RATHER THAN READING THE MASTER. Pure, so it is the same function
 *  on the server (which builds the map from `ranks`) and in the browser (where `useRankOptions`
 *  already holds every rank in one bounded page). A version of this that queried would be
 *  unusable in one of the two places it has to work.
 *
 *  ⚠️ A GROUP WHOSE MEMBER DOES NOT EXIST IN THE MASTER CONTRIBUTES NOTHING RATHER THAN
 *  THROWING. An expansion runs on a read path — the picker, the guard — and a master missing a
 *  row (a fresh database, a partial seed) must narrow the offer, never break the screen. The
 *  place that refuses a missing code is `seed:ranks`, which names it while writing.
 *
 *  ⚠️ AND THE RESULT IS DE-DUPLICATED. `{group: AC}` alongside `{rank: 'AC(W)'}` is a legal and
 *  meaningful pair — "any AC, and I am saying AC(W) explicitly" — and the union must not carry
 *  that rank twice into a `$in`.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const expandRankSelection = (
  selection: { rankIds: readonly string[]; groupKeys: readonly string[] },
  rankIdByCode: ReadonlyMap<string, string>,
): string[] => {
  const out = new Set<string>(selection.rankIds);
  for (const key of selection.groupKeys) {
    const group = RANK_GROUPS.find((row) => row.key === key);
    if (!group) continue;
    for (const code of group.rankCodes) {
      const id = rankIdByCode.get(code);
      if (id !== undefined) out.add(id);
    }
  }
  return [...out];
};
